import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { postDataRequest } from '../../api/serviceMethods';
import { PRESIGNED_UPLOAD_URL } from '../../api/URLs';

/**
 * Custom Rich Text Editor with 2-step Presigned OSS Image Upload
 * 
 * @param {string} value - HTML string content
 * @param {function} onChange - Callback triggered on content change
 */
const MyRichTextEditor = ({ value = '', onChange }) => {
  const quillRef = useRef(null);

  // Helper function to handle 2-step Presigned URL Upload to OSS
  const uploadImageToOSS = async (file) => {
    // Step 1: Request presigned URL from your backend API
    const res = await postDataRequest(PRESIGNED_UPLOAD_URL, { fileName: file.name });
    const responseData = res?.data || res;
    const signUrl = responseData?.signUrl;

    if (!signUrl) {
      throw new Error(responseData?.message || "Failed to get presigned upload URL.");
    }

    // Step 2: Upload file directly to OSS via PUT request
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload to OSS failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed due to network error.'));
      xhr.send(file);
    });

    // Extract clean URL by removing query params
    return signUrl.split('?')[0];
  };

  // Custom toolbar image handler
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        // 1. Upload to OSS using presigned URL
        const fileUrl = await uploadImageToOSS(file);

        // 2. Get Quill editor instance and current cursor position
        const quill = quillRef.current?.getEditor();
        const range = quill?.getSelection(true);

        if (quill && range) {
          // 3. Insert uploaded image URL at cursor position
          quill.insertEmbed(range.index, 'image', fileUrl);

          // 4. Move cursor past inserted image
          quill.setSelection(range.index + 1);
        }
      } catch (error) {
        console.error('RichTextEditor OSS Upload Error:', error);
      }
    };
  };

  // Wrap module settings in useMemo to maintain stable reference and prevent focus loss
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean']
        ],
        handlers: {
          image: imageHandler, // Custom handler for image button
        },
      },
    }),
    []
  );

  return (
    <div className="my-rich-text-editor">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
      />
    </div>
  );
};

export default MyRichTextEditor;